/**
 * The Word Combiner: portmanteau blends of two words or names.
 *
 * A blend takes the start of one word and the end of the other: breakfast + lunch →
 * brunch, motor + hotel → motel. Every way of cutting the two words is generated — a
 * front part of A joined to a back part of B — and each is scored on three things:
 *
 * - **Overlap.** Letters that sit on both sides of the join in both words. In
 *   mo|tel the join falls inside the shared "ot" of mo-t-or and ho-t-el, so the blend can
 *   be read as either word continuing into the other. More shared letters make a
 *   smoother join, and this is the main ranking factor.
 * - **Shape.** Real blends are usually about half as long as the two words together,
 *   and the commonest recipe is the opening consonants of one word plus everything from
 *   the other word's first vowel on (br-eakfast + l-unch → br + unch).
 * - **Sound.** Whether the result is easy to say: it needs a vowel, and long runs of
 *   consonants or vowels are penalized, while a join between a consonant and a vowel is
 *   rewarded.
 *
 * It judges letters, not sounds, so treat the order as a shortlist. The ranking never
 * looks at a dictionary; the page marks the blends that are already ordinary words
 * (from the ENABLE list) separately, since "motel" and "smog" are blends that stuck.
 */

export interface CombinerOptions {
  /** Fewest letters a word must contribute when it is cut (a word kept whole always counts). */
  minPart: number;
  /** Also blend in the order B + A. */
  bothOrders: boolean;
}

export interface Blend {
  word: string;
  /** The front part, taken from the first word. */
  head: string;
  /** The back part, taken from the second word. */
  tail: string;
  /** Letters of the head that are also the letters just before the cut in the other word. */
  sharedHead: number;
  /** Letters at the start of the tail that match the first word's letters after its cut. */
  sharedTail: number;
  /** Which word comes first: "ab" is A + B. */
  order: "ab" | "ba";
  score: number;
}

const RE_NOT_LETTER = /[^\p{L}]/gu;
const VOWELS = new Set("aeiouyàáâãäåæèéêëìíîïòóôõöøùúûüýÿœ");
const MAX_COMBINED = 150;

export type CombinerInput =
  | { ok: true; first: string; second: string }
  | { ok: false; reason: "empty" | "short" | "same" };

/** Letters only, lowercase; each word needs at least two letters. */
export function cleanWord(text: string): string {
  return text.replace(RE_NOT_LETTER, "").toLowerCase();
}

export function parseCombiner(
  firstText: string,
  secondText: string
): CombinerInput {
  const first = cleanWord(firstText);
  const second = cleanWord(secondText);
  if (!(first && second)) {
    return { ok: false, reason: "empty" };
  }
  if (first.length < 2 || second.length < 2) {
    return { ok: false, reason: "short" };
  }
  if (first === second) {
    return { ok: false, reason: "same" };
  }
  return { ok: true, first, second };
}

const isVowel = (letter: string) => VOWELS.has(letter);

function longestRun(word: string, vowel: boolean): number {
  let longest = 0;
  let run = 0;
  for (const letter of word) {
    run = isVowel(letter) === vowel ? run + 1 : 0;
    longest = Math.max(longest, run);
  }
  return longest;
}

/** Letters shared at the join: a common ending before the cuts, a common start after. */
function sharedAround(a: string, i: number, b: string, j: number) {
  let head = 0;
  while (head < i && head < j && a[i - 1 - head] === b[j - 1 - head]) {
    head += 1;
  }
  let tail = 0;
  while (
    i + tail < a.length &&
    j + tail < b.length &&
    a[i + tail] === b[j + tail]
  ) {
    tail += 1;
  }
  return { head, tail };
}

function soundScore(word: string, headEnd: string, tailStart: string): number {
  if (![...word].some(isVowel)) {
    return Number.NEGATIVE_INFINITY;
  }
  let score = 0;
  const consonants = longestRun(word, false);
  const vowels = longestRun(word, true);
  if (consonants >= 4) {
    score -= 6;
  } else if (consonants === 3) {
    score -= 1;
  }
  if (vowels >= 4) {
    score -= 4;
  } else if (vowels === 3) {
    score -= 1;
  }
  if (isVowel(headEnd) !== isVowel(tailStart)) {
    score += 2;
  }
  return score;
}

/** Index of a word's first vowel; a leading y is a consonant (yellow), a later y a vowel. */
function firstVowel(word: string): number {
  for (let i = 0; i < word.length; i += 1) {
    if (isVowel(word[i]) && !(i === 0 && word[i] === "y")) {
      return i;
    }
  }
  return -1;
}

/** The blend that swaps onsets: a's opening consonants + b from its first vowel. */
function onsetBlend(a: string, b: string): string | null {
  const onset = firstVowel(a);
  const rime = firstVowel(b);
  return onset > 0 && rime >= 0 ? a.slice(0, onset) + b.slice(rime) : null;
}

/** 0 when the blend is half as long as the two words together, up to 6 at that ideal. */
function lengthFit(blendLength: number, total: number): number {
  const miss = Math.abs(blendLength / total - 0.55) / 0.45;
  return 6 * (1 - Math.min(1, miss));
}

interface Cut {
  a: string;
  b: string;
  i: number;
  j: number;
  word: string;
  swapped: string | null;
  onsetEnd: number;
  rimeStart: number;
}

function scoreCut(cut: Cut, shared: number, order: "ab" | "ba"): number {
  const { a, b, i, j, word } = cut;
  const keptA = i / a.length;
  const keptB = (b.length - j) / b.length;
  return (
    4 * shared +
    lengthFit(word.length, a.length + b.length) +
    2 * Math.min(keptA, keptB) +
    (word === cut.swapped ? 3 : 0) +
    // Between two splits that spell the same blend, show the more natural recipe:
    // the opening consonants and the rest from a vowel, or one word kept whole.
    (i === cut.onsetEnd && j === cut.rimeStart ? 0.5 : 0) +
    (i === a.length || j === 0 ? 0.3 : 0) +
    (order === "ab" ? 0.5 : 0) +
    soundScore(word, a[i - 1], b[j])
  );
}

function blendsOf(
  a: string,
  b: string,
  order: "ab" | "ba",
  minPart: number
): Blend[] {
  const needA = Math.min(minPart, a.length);
  const needB = Math.min(minPart, b.length);
  const swapped = onsetBlend(a, b);
  const onsetEnd = firstVowel(a);
  const rimeStart = firstVowel(b);
  const found: Blend[] = [];
  for (let i = needA; i <= a.length; i += 1) {
    for (let j = 0; j <= b.length - needB; j += 1) {
      const word = a.slice(0, i) + b.slice(j);
      if ((i === a.length && j === 0) || word === a || word === b) {
        continue;
      }
      const { head, tail } = sharedAround(a, i, b, j);
      const cut = { a, b, i, j, word, swapped, onsetEnd, rimeStart };
      found.push({
        word,
        head: a.slice(0, i),
        tail: b.slice(j),
        sharedHead: head,
        sharedTail: tail,
        order,
        score: scoreCut(cut, head + tail, order),
      });
    }
  }
  return found;
}

/** The best blends, highest score first; one entry per distinct result. */
export function combine(
  first: string,
  second: string,
  options: CombinerOptions
): Blend[] {
  const all = [
    ...blendsOf(first, second, "ab", options.minPart),
    ...(options.bothOrders
      ? blendsOf(second, first, "ba", options.minPart)
      : []),
  ];
  const best = new Map<string, Blend>();
  for (const blend of all) {
    if (blend.score === Number.NEGATIVE_INFINITY) {
      continue;
    }
    const kept = best.get(blend.word);
    if (!kept || blend.score > kept.score) {
      best.set(blend.word, blend);
    }
  }
  return [...best.values()]
    .sort((x, y) => y.score - x.score || x.word.localeCompare(y.word))
    .slice(0, MAX_COMBINED);
}
